import { useState, useRef, useEffect } from "react";
import { ChevronDown, Send, Square, Paperclip, Plus, Mic, Headphones } from "lucide-react";
import type { ModelEntry, ChatConfig, EffortLevel } from "../shared/chat-config";
import { useI18n } from "../i18n";

const EFFORT_LEVELS: EffortLevel[] = ['low', 'medium', 'high', 'xhigh', 'max'];
const EFFORT_LABELS: Record<EffortLevel, string> = {
  low: 'low',
  medium: 'med',
  high: 'high',
  xhigh: 'xhigh',
  max: 'max',
};
// These levels activate extended thinking on Claude models
const THINKING_LEVELS = new Set<EffortLevel>(['high', 'xhigh', 'max']);

interface InputControlsProps {
  config: ChatConfig;
  currentModel: ModelEntry;
  availableModels: ModelEntry[];
  onModelChange: (modelId: string) => void;
  onEffortChange: (effort: EffortLevel) => void;
  isLoading: boolean;
  switching?: boolean;
  onStop: () => void;
  onSend: () => void;
  onSummarize: () => void;
  onAttach: () => void;
  disabled: boolean;
  uploading?: boolean;
  voiceMode?: boolean;
  onToggleVoiceMode?: () => void;
  onMicToggle?: () => void;
  isListening?: boolean;
  isTranscribing?: boolean;
}

export function InputControls({
  config,
  currentModel,
  availableModels,
  onModelChange,
  onEffortChange,
  isLoading,
  switching,
  onStop,
  onSend,
  onSummarize,
  onAttach,
  disabled,
  uploading,
  voiceMode,
  onToggleVoiceMode,
  onMicToggle,
  isListening,
  isTranscribing,
}: InputControlsProps) {
  const { t } = useI18n();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [popoverStyle, setPopoverStyle] = useState<React.CSSProperties>({});
  const dropdownRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const plusBtnRef = useRef<HTMLButtonElement>(null);

  // supportsEffort is required on ModelEntry (always set in seed + live registry).
  // Read it directly — no provider-based fallback needed.
  const showEffortPill = currentModel.supportsEffort;

  function handleEffortClick() {
    const currentIndex = EFFORT_LEVELS.indexOf(config.effort);
    const nextIndex = (currentIndex + 1) % EFFORT_LEVELS.length;
    onEffortChange(EFFORT_LEVELS[nextIndex]);
  }

  // Close model dropdown on outside click
  useEffect(() => {
    if (!dropdownOpen) return;
    function onClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [dropdownOpen]);

  // Position the popover above the + button using fixed positioning.
  // Mobile uses CSS bottom-sheet positioning, so only compute on desktop.
  useEffect(() => {
    if (!popoverOpen || !plusBtnRef.current) return;
    if (window.innerWidth <= 640) {
      setPopoverStyle({});
      return;
    }
    const rect = plusBtnRef.current.getBoundingClientRect();
    setPopoverStyle({
      bottom: window.innerHeight - rect.top + 6,
      left: rect.left,
    });
  }, [popoverOpen]);

  // Close + popover on outside click or Escape
  useEffect(() => {
    if (!popoverOpen) return;
    function onClickOutside(e: MouseEvent) {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node) &&
        plusBtnRef.current &&
        !plusBtnRef.current.contains(e.target as Node)
      ) {
        setPopoverOpen(false);
      }
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") setPopoverOpen(false);
    }
    document.addEventListener("mousedown", onClickOutside);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onClickOutside);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [popoverOpen]);

  return (
    <div className="input-controls">
      {/* ── Left: + button (pinned) ── */}
      <div className="input-controls__more-wrap">
        <button
          ref={plusBtnRef}
          className={`input-controls__more-btn ${popoverOpen ? "active" : ""}`}
          onClick={() => setPopoverOpen(o => !o)}
          aria-label={t("controls.moreActions")}
          aria-haspopup="menu"
          aria-expanded={popoverOpen}
        >
          <Plus size={16} />
        </button>
        {popoverOpen && (
          <div
            ref={popoverRef}
            className="input-controls__popover"
            role="menu"
            style={popoverStyle}
          >
            {/* Attach files */}
            <button
              role="menuitem"
              className="input-controls__popover-item"
              onClick={() => { onAttach(); setPopoverOpen(false); }}
              aria-label={t("attachments.add")}
              disabled={isLoading}
            >
              <Paperclip size={14} style={{ marginRight: 8, flexShrink: 0 }} />
              {t("attachments.add")}
            </button>

            {/* Summarize */}
            <button
              role="menuitem"
              className="input-controls__popover-item"
              onClick={() => { onSummarize(); setPopoverOpen(false); }}
              disabled={isLoading || disabled}
              title={t("controls.summarize")}
            >
              {t("controls.summarize")}
            </button>

            {/* Voice mode */}
            {onToggleVoiceMode && (
              <button
                role="menuitem"
                className="input-controls__popover-item"
                onClick={() => { onToggleVoiceMode(); setPopoverOpen(false); }}
                disabled={isLoading || disabled}
              >
                <Headphones size={14} style={{ marginRight: 8, flexShrink: 0 }} />
                Voice mode
              </button>
            )}
          </div>
        )}
      </div>

      {/* ── Middle: effort pill (models that support effort) ── */}
      <div className="input-controls__middle">
        {showEffortPill && (
          <button
            className="input-controls__effort-pill"
            data-thinking={THINKING_LEVELS.has(config.effort) ? "true" : "false"}
            onClick={handleEffortClick}
            disabled={isLoading || switching}
            title={`Effort: ${config.effort}. Click to cycle.`}
            aria-label={`Effort level: ${config.effort}`}
          >
            {EFFORT_LABELS[config.effort]}
          </button>
        )}
      </div>

      {/* ── Right cluster: Model picker · Mic · Send/Stop ── */}

      {/* Model picker */}
      <div className="input-controls__model" ref={dropdownRef}>
        <button
          className="input-controls__model-btn"
          onClick={() => setDropdownOpen(o => !o)}
          title={t("controls.model")}
        >
          <span>{currentModel.label}</span>
          <ChevronDown size={12} aria-hidden="true" />
        </button>
        {dropdownOpen && (
          <div className="input-controls__dropdown">
            {availableModels.map(m => (
              <button
                key={m.id}
                className={`input-controls__dropdown-item ${m.id === currentModel.id ? "active" : ""}`}
                onClick={() => { onModelChange(m.id); setDropdownOpen(false); }}
              >
                <span className="input-controls__dropdown-label">{m.label}</span>
                <span className="input-controls__dropdown-provider">{m.provider}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Mic button — speech-to-text dictation */}
      {onMicToggle && (
        <button
          className={`input-controls__mic-btn${isListening ? " listening" : ""}${isTranscribing ? " transcribing" : ""}`}
          onClick={onMicToggle}
          aria-label={isListening ? "Stop dictation" : "Dictate"}
          aria-pressed={isListening}
          title={isListening ? "Stop dictation" : "Dictate"}
        >
          <Mic size={16} />
        </button>
      )}

      {/* Send / Stop — pinned right */}
      {isLoading ? (
        <button
          className="chat-send-btn stop-btn"
          onClick={onStop}
          aria-label={t("controls.stopGenerating")}
          title={t("controls.stopGenerating")}
        >
          <Square size={16} />
        </button>
      ) : (
        <button
          className={`chat-send-btn${uploading ? " uploading" : ""}${switching ? " switching" : ""}`}
          onClick={onSend}
          disabled={disabled || uploading || switching}
          aria-label={switching ? "Switching model…" : uploading ? t("controls.uploading") : t("controls.sendMessage")}
          title={switching ? "Switching model…" : uploading ? t("controls.uploading") : t("controls.sendMessage")}
        >
          <Send size={16} />
        </button>
      )}
    </div>
  );
}
